import type { StandardTaskID } from '../../cross-app-shared/types/standard-tasks.ts';
import type {
  EnvironmentalTaskCategory,
  EnvironmentalTaskInspectionType,
  ExternalTaskConcrete,
  FacilityHierarchyConfig,
  ISO8601DateOnly,
  ISO8601Timestamp,
  Method9Values,
  Notification,
  NotificationEscalationDetails,
  Role,
  Schedule,
  ScheduleTemplate,
  Task,
  TaskCustomField,
  TaskCustomFieldConfirmPermitStatus,
  TaskCustomFieldDefaultValue,
  TaskCustomFieldDetermination,
  TaskCustomFieldFile,
  TaskCustomFieldImage,
  TaskCustomFieldLock,
  TaskCustomFieldMethod9,
  TaskCustomFieldRepeater,
  TaskCustomFieldSelect,
  TaskCustomFieldText,
  TaskCustomFieldTextarea,
  TaskCustomFieldWithDefaultValue,
  TaskEscalationHierarchy,
  TaskGenerator,
  TaskGeneratorDeficiency,
  TaskGeneratorNew,
  TaskTemplate,
  TaskTemplateNew,
  TrackedStatus,
} from '../types/compliance.ts';
import { generateId } from '../../cross-app-shared/services/generate-id.ts';
import {
  dateLocal,
  dateNow,
  dateStrDurationAdd,
  durationIsEntirelyPositive,
  getSimpleDurationLengthInDays,
  timestamp,
  timestampAtTimeInTimeZone,
} from '../../cross-app-shared/services/dates-and-durations.ts';
import { scheduleToNotificationDates } from './notification-schedules.ts';
import type { Email, Uid } from '../../cross-app-shared/types/common.ts';
import type { Facility, User } from '../types/common.ts';
import { newSchedule, newScheduleTemplate } from './schedule.ts';
import { jsonStableStringify } from './json-stable-stringify.ts';
import { uidsToUsers } from './user.ts';
import { method9ObservationsAreComplete } from './method9.ts';

export type TaskNotificationsUpdate<DeletionSentinel> = Record<ISO8601DateOnly, Notification | DeletionSentinel>;

export function newTaskGenerator(facilityID: Facility['facilityID'], createdBy: Uid): TaskGeneratorNew {
  return {
    taskGeneratorID: generateId('generator'),
    taskTemplateID: null,
    standardTaskIds: [],
    facilityID,
    parentFolderIDs: [],
    title: '',
    description: '',
    inspectionType: 'Other',
    category: 'Other',
    schedule: newSchedule(createdBy),
    tracked: false,
    owner: createdBy,
    involved: [],
    ownerConfigured: createdBy,
    involvedConfigured: [],
    customFieldDefinitions: [],
  };
}

export function newTaskTemplate(
  createdBy: Uid,
  associatedFacilityID: Facility['facilityID'] | null = null,
): TaskTemplateNew {
  return {
    taskTemplateID: generateId('template'),
    standardTaskIds: [],
    editableBy: [createdBy],
    isStandardTaskTemplate: false,
    title: '',
    description: '',
    inspectionType: 'Other',
    category: 'Other',
    schedules: [newScheduleTemplate(createdBy)],
    isFacilityScheduleChoiceAvailable: false,
    tracked: true,
    owner: {},
    involved: {},
    facilities: associatedFacilityID ? [associatedFacilityID] : [],
    customFieldDefinitions: [],
    canGeneratorsCustomizeFields: false,
    taskGeneratorsInstantiated: 0,
    taskGeneratorLastInstantiatedAt: null,
  };
}

export function newCustomField(type: TaskCustomField['type']): TaskCustomFieldWithDefaultValue {
  switch (type) {
    case 'select':
      return newCustomFieldSelect();
    case 'text':
      return newCustomFieldText();
    case 'textarea':
      return newCustomFieldTextarea();
    case 'file':
      return newCustomFieldFile();
    case 'image':
      return newCustomFieldImage();
    case 'method9':
      return newCustomFieldMethod9();
    case 'confirmPermitStatus':
      return newCustomFieldConfirmPermitStatus();
    case 'determination':
      return newCustomFieldDetermination();
    case 'repeater':
      return newCustomFieldRepeater();
    case 'lock':
      return newCustomFieldLock();
  }
}

export function newCustomFieldSelect(): TaskCustomFieldSelect & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'select',
    options: [],
    multiple: false,
    defaultValue: '',
  };
}

export function newCustomFieldText(): TaskCustomFieldText & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'text',
    defaultValue: '',
  };
}

export function newCustomFieldTextarea(): TaskCustomFieldTextarea & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'textarea',
    defaultValue: '',
  };
}

export function newCustomFieldFile(): TaskCustomFieldFile & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'file',
    multiple: true,
    defaultValue: [],
    required: false,
  };
}

export function newCustomFieldImage(): TaskCustomFieldImage & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'image',
    multiple: true,
    defaultValue: [],
  };
}

export function newCustomFieldLock(): TaskCustomFieldLock & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: 'Task Completion Lock',
    helpText: '',
    type: 'lock',
    // true means locked. We default to locked as the intended use case is a task that is locked until the system says
    // it is completable:
    defaultValue: true,
  };
}

export function newCustomFieldMethod9(): TaskCustomFieldMethod9 & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'method9',
    stages: [],
    defaultValue: null,
  };
}

export function newCustomFieldConfirmPermitStatus(): TaskCustomFieldConfirmPermitStatus & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'confirmPermitStatus',
    defaultValue: {},
  };
}

export function newCustomFieldDetermination(): TaskCustomFieldDetermination & TaskCustomFieldWithDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'determination',
    templateID: '',
    defaultValue: null,
  };
}

export function newCustomFieldRepeater(): TaskCustomFieldRepeater & TaskCustomFieldDefaultValue {
  return {
    fieldID: generateId('cf'),
    label: '',
    helpText: '',
    type: 'repeater',
    instances: [],
    templateFields: [],
    defaultValue: null,
  };
}

export function repeaterExpand(repeater: TaskCustomFieldRepeater): TaskCustomFieldWithDefaultValue[] {
  const result: TaskCustomFieldWithDefaultValue[] = [];

  for (const instance of repeater.instances) {
    for (const templateField of repeater.templateFields) {
      const instantiatedField = JSON.parse(JSON.stringify(templateField)) as typeof templateField;
      instantiatedField.fieldID = `${repeater.fieldID}_${templateField.fieldID}_${instance.instanceID}`;

      const hashRegex = /#/g;
      instantiatedField.label = instantiatedField.label.replace(hashRegex, instance.name);
      instantiatedField.helpText = instantiatedField.helpText.replace(hashRegex, instance.name);

      instantiatedField.repeaterMetadata = {
        parentFieldID: repeater.fieldID,
        instance,
      };

      result.push(instantiatedField);
    }
  }

  return result;
}

export function customFieldsExpandAllRepeaters(
  customFields: TaskCustomFieldWithDefaultValue[],
): TaskCustomFieldWithDefaultValue[] {
  const expandedFields: TaskCustomFieldWithDefaultValue[] = [];

  for (const field of customFields) {
    if (field.type === 'repeater') {
      expandedFields.push(...repeaterExpand(field));
    } else {
      expandedFields.push(field);
    }
  }

  return expandedFields;
}

export const environmentalTaskCategories: EnvironmentalTaskCategory[] = [
  'Air',
  'Corp',
  'DOT',
  'EMS',
  'EPCRA',
  'Maintenance',
  'Other',
  'Radiation',
  'Safety',
  'SPCC',
  'Waste',
  'Water',
];
export const environmentalTaskInspectionTypes: EnvironmentalTaskInspectionType[] = [
  'Calibration',
  'Fee',
  'Inspection',
  'Monitoring',
  'New Permit',
  'Notification',
  'Other',
  'Permit Renewal',
  'Plan',
  'Registration',
  'Report',
  'Safety',
  'Testing',
  'Training',
  'Update/Review',
];

export type TaskSource = 'compliance' | 'determinations' | 'permit' | 'hazwaste';

export const externalTaskTypeToTaskSource: Record<ExternalTaskConcrete['type'] | '', TaskSource> = {
  '': 'compliance',
  applicableStandard: 'determinations',
  permitIndexing: 'permit',
  permitCondition: 'permit',
  permitsReviewConditionalConditions: 'permit',
  determinationComplianceIssue: 'determinations',
  hazWasteAccumulationDeadline: 'hazwaste',
  hazWasteStreamReview: 'hazwaste',
  hazWasteContainerInspection: 'hazwaste',
  hazWasteTankInspection: 'hazwaste',
  hazWasteInspectionFinding: 'hazwaste',
};

export function taskEmailsGet(
  task: Pick<Task, 'dueAt'>,
  schedule: Schedule | null,
  tracked: TrackedStatus,
  hierarchy: TaskEscalationHierarchy,
  timeZone: string,
  now: ISO8601Timestamp,
): { emails: Set<Email>; uids: Set<Uid>; escalations: NotificationEscalationDetails[] } {
  const emails = new Set<Email>();
  const uids = new Set<Uid>();
  const escalations: NotificationEscalationDetails[] = [];

  for (const { email, uid } of hierarchy.owner.users.concat(hierarchy.involved.users)) {
    uids.add(uid);
    emails.add(email);
  }

  if (schedule && tracked) {
    const nowAsDate = dateLocal(now, timeZone);

    for (const [key, { users, escalates, name, source }] of Object.entries(hierarchy)) {
      if (key === 'owner' || key === 'involved' || !escalates) {
        continue;
      }

      const escalationDate = dateStrDurationAdd(task.dueAt, escalates);
      if (escalationDate > nowAsDate) {
        continue;
      }

      for (const { email, uid } of users) {
        uids.add(uid);
        emails.add(email);
      }

      escalations.push({
        escalationID: key as Exclude<keyof TaskEscalationHierarchy, 'owner' | 'involved'>,
        name,
        source,
      });
    }
  }

  return { emails, uids, escalations };
}

export function taskNotificationGetID(
  taskID: Task['taskID'],
  date: ISO8601DateOnly,
): Notification['taskNotificationID'] {
  return `tn_${taskID}_${date}`;
}

export function taskRefreshNotifications<DeletionSentinel>(
  task: Pick<Task, 'owner' | 'involved' | 'dueAt' | 'dueAtOrig' | 'createdAt' | 'createdBy' | 'completedAt' | 'taskID'>,
  notifications: Record<ISO8601DateOnly, Notification>,
  schedule: Schedule,
  tracked: TrackedStatus,
  generatorImportedAt: ISO8601DateOnly | null | undefined = null,
  hierarchy: TaskEscalationHierarchy,
  deletionSentinel: DeletionSentinel,
  expectedEmailLimit: number,
  timeZone: string,
): TaskNotificationsUpdate<DeletionSentinel> | null {
  // Don't bother computing updates for completed tasks:
  if (task.completedAt) {
    return null;
  }

  const result: TaskNotificationsUpdate<DeletionSentinel> = {};
  let cutOff = '1970-01-01' as ISO8601DateOnly;

  for (const [key, value] of Object.entries(notifications)) {
    if (value.type === 'expected') {
      result[key as ISO8601DateOnly] = deletionSentinel;
    } else if (value.type === 'sent') {
      const date = dateLocal(new Date(value.sentAt), timeZone);
      if (date > cutOff) {
        cutOff = date;
      }
    }
  }

  if (generatorImportedAt && generatorImportedAt > cutOff) {
    cutOff = generatorImportedAt;
  }

  const notificationsPossible = scheduleToNotificationDates(schedule, task.dueAt, tracked, hierarchy);

  let expectedEmailsAdded = 0;
  for (const notification of notificationsPossible) {
    const existingType = notifications[notification.date]?.type;

    // No need to update concrete events, we want to preserve them:
    if (existingType === 'sent' || existingType === 'failed') {
      continue;
    }

    // If the notification should've been sent _before_ our most notification was successfully sent, skip it so we
    // don't end up duplicating emails or anything weird:
    if (notification.date <= cutOff) {
      continue;
    }

    const {
      emails: emailsSet,
      uids: uidsSet,
      escalations,
    } = taskEmailsGet(
      task,
      schedule,
      tracked,
      hierarchy,
      timeZone,
      timestampAtTimeInTimeZone({ date: notification.date, hour: 0, timeZone }),
    );

    // For the very first task in a series, if:
    //   1. The only person who would be sent an email is the person who created the task.
    //   2. That email would be sent on the same day they created it.
    // Don't send them an email:
    if (
      (task.dueAt === schedule.dueAtFirst.date || task.dueAtOrig === schedule.dueAtFirst.date) &&
      dateLocal(task.createdAt, timeZone) >= notification.date &&
      task.createdBy !== 'system' &&
      emailsSet.size === 1 &&
      uidsSet.size === 1 &&
      Array.from(uidsSet)[0] === task.createdBy
    ) {
      continue;
    }

    const emails = Array.from(emailsSet).sort();
    const uids = Array.from(uidsSet).sort();

    const notificationUpdateValue: Notification = {
      date: notification.date,
      taskID: task.taskID,
      taskNotificationID: `tn_${task.taskID}_${notification.date}`,
      type: 'expected',
      scheduleID: schedule.scheduleID,
      scheduleNotificationID: notification.scheduleNotificationID,
      users: uids,
      emails,
      // Schedule notifications for midnight in the anchored time zone:
      scheduledToSendAt: timestampAtTimeInTimeZone({
        date: notification.date,
        hour: 0,
        timeZone,
      }),
    };

    if (escalations.length > 0) {
      notificationUpdateValue.escalations = escalations;
    }

    if (
      !notifications[notification.date] ||
      jsonStableStringify(notifications[notification.date]) !== jsonStableStringify(notificationUpdateValue)
    ) {
      result[notification.date] = notificationUpdateValue;
    } else {
      delete result[notification.date];
    }

    expectedEmailsAdded += 1;
    if (expectedEmailsAdded >= expectedEmailLimit) {
      break;
    }
  }

  if (Object.keys(result).length === 0) {
    return null;
  }

  return result;
}

export function* scheduleGenerateTaskDueDates(
  schedule: Schedule,
  dontGenerateTasksBefore: null | undefined | ISO8601DateOnly,
  dueDatesExcluded: Set<ISO8601DateOnly>,
  alreadyCreatedCount: number,
  dueDateAdjustments: Record<ISO8601DateOnly, ISO8601DateOnly>,
): Generator<{ dueAt: ISO8601DateOnly; createdAt: ISO8601DateOnly }, void, void> {
  if (!durationIsEntirelyPositive(schedule.frequency)) {
    return;
  }

  let taskCreationDateCurrent = dateStrDurationAdd(schedule.dueAtFirst.date, schedule.create);
  let taskDueDateCurrent = schedule.dueAtFirst.date;
  let multiplier = 1;

  let created = alreadyCreatedCount;
  while (schedule.limit > created) {
    if (!dontGenerateTasksBefore || taskDueDateCurrent >= dontGenerateTasksBefore) {
      if (schedule.generateOnlyOnCompletion || !dueDatesExcluded.has(taskDueDateCurrent)) {
        yield { dueAt: taskDueDateCurrent, createdAt: taskCreationDateCurrent };
        created += 1;
      }
    }

    if (schedule.frequency.locationInWeek || schedule.frequency.locationInMonth) {
      taskDueDateCurrent = dateStrDurationAdd(taskDueDateCurrent, schedule.frequency);
    } else {
      // We use a multiplier instead of successively adding duration to current because otherwise dates anchored at the
      // end of the month will drift towards the 28th rather than staying at the end of the month:
      taskDueDateCurrent = dateStrDurationAdd(schedule.dueAtFirst.date, {
        ...schedule.frequency,
        years: schedule.frequency.years * multiplier,
        months: schedule.frequency.months * multiplier,
        weeks: schedule.frequency.weeks * multiplier,
        days: schedule.frequency.days * multiplier,
      });
      multiplier += 1;
    }

    // If a future task is now due sooner than it originally would be, create it sooner so that we can send
    // notifications against it and so users can complete it:
    const dueAtAdjusted = dueDateAdjustments[taskDueDateCurrent];
    if (dueAtAdjusted && dueAtAdjusted < taskDueDateCurrent) {
      taskCreationDateCurrent = dateStrDurationAdd(dueAtAdjusted, schedule.create);
    } else {
      taskCreationDateCurrent = dateStrDurationAdd(taskDueDateCurrent, schedule.create);
    }

    // When the generateOnlyOnCompletion flag is set, we only ever generate 1 at a time, and we update dueAtFirst on
    // task completion in order to cause the next one to generate. Thus, after one iteration we can stop:
    if (schedule.generateOnlyOnCompletion) {
      break;
    }
  }
}

export function scheduleToSetOfTaskDueDates(
  schedule: Schedule,
  dontGenerateTasksBefore: ISO8601DateOnly | undefined | null,
  dueDatesExcluded: Set<ISO8601DateOnly>,
  now: Date,
  timeZone: string,
  dueDateAdjustments: Record<ISO8601DateOnly, ISO8601DateOnly>,
): { dueDates: Set<ISO8601DateOnly>; nextCreationDate: ISO8601DateOnly; nextDueDate: ISO8601DateOnly } {
  const dueDates = new Set<ISO8601DateOnly>();
  const taskCreationDateFirst = dateStrDurationAdd(schedule.dueAtFirst.date, schedule.create);

  // If we aren't quite at the first creation date, just fast-forward there so we always create at least 1 task:
  let endDateStr = dateLocal(now, timeZone);
  if (endDateStr < taskCreationDateFirst) {
    endDateStr = taskCreationDateFirst;
  }

  const generator = scheduleGenerateTaskDueDates(
    schedule,
    dontGenerateTasksBefore,
    dueDatesExcluded,
    0,
    dueDateAdjustments,
  );
  let dueAtLast = schedule.dueAtFirst.date;
  for (const { dueAt, createdAt } of generator) {
    dueAtLast = dueAt;
    if (createdAt > endDateStr) {
      break;
    }
    dueDates.add(dueAt);
  }

  const next = generator.next();
  if (next.done) {
    return { dueDates, nextCreationDate: dateStrDurationAdd(dueAtLast, schedule.create), nextDueDate: dueAtLast };
  } else {
    return { dueDates, nextCreationDate: next.value.createdAt, nextDueDate: next.value.dueAt };
  }
}

/**
 * Given a task generator and a list of all tasks it has already generated, this function returns new Tasks to create.
 *
 * The algorithm is simple:
 *
 * - Start from the first due date and task creation date.
 * - Keep walking the due date and creation date forward until the next creation date is after today's date, collecting
 *   each task due date we see along the way.
 * - Compute the difference between the due dates we have and the due dates we expect.
 * - Return a new list of tasks with due dates from the list above.
 *
 * This algorithm was selected because:
 *
 * - It is easy to reason about.
 * - It is easy to communicate about.
 * - If task generation ever fails, it will generate missed tasks when the system is fixed.
 * - It allows historical tasks to be created with due dates in the past, if needed.
 * - Existing tasks will never be automatically deleted, preserving any in-progress work and ensuring deletion requires
 *   intentional action.
 *
 * However, it has downsides:
 *
 * - We will always walk through all due dates, which is somewhat inefficient. But, attempting to skip straight to the
 *   next task creation date could cause issues with dates getting out of sync whenever the schedule changes.
 * - Naive changes to the schedule could cause many tasks to be generated. i.e. shifting schedule.due.anchorDate forward
 *   by a day will duplicate all existing tasks. We should mitigate this by computing what will happen when a schedule
 *   changes, and prompting the user for confirmation. Additionally, we may wish to enforce that the anchorDate is
 *   always in the future when updating a task schedule.
 */
export function taskGeneratorGenerateTasks(
  generator: TaskGenerator | TaskGeneratorNew,
  taskDueDatesExisting: ISO8601DateOnly[],
  createdBy: Task['createdBy'],
  timeZone: string,
  now = dateNow(),
): { tasks: Task[]; nextCreationDate: ISO8601DateOnly } {
  const { dueDates: dueDatesSet, nextCreationDate } = scheduleToSetOfTaskDueDates(
    generator.schedule,
    generator.dontGenerateTasksBefore,
    new Set(generator.dueDatesExcluded ?? []),
    now,
    timeZone,
    generator.dueDateAdjustments ?? {},
  );

  for (const dueDate of taskDueDatesExisting) {
    dueDatesSet.delete(dueDate);
  }

  const dueDates = Array.from(dueDatesSet).sort();
  while (dueDates.length > 0 && taskDueDatesExisting.length + dueDates.length > generator.schedule.limit) {
    dueDates.pop();
  }

  return {
    nextCreationDate,
    tasks: dueDates.map((dueAt): Task => {
      return taskFromGeneratorAndDueAt(generator, dueAt, createdBy, timeZone, now);
    }),
  };
}

export function taskFromGeneratorAndDueAt(
  generator: TaskGenerator | TaskGeneratorNew,
  dueAt: ISO8601DateOnly,
  createdBy: Task['createdBy'],
  timeZone: string,
  now: Date,
): Task {
  const today = dateLocal(now, timeZone);
  const customFieldValues: Task['customFieldValues'] = {};

  for (const field of customFieldsExpandAllRepeaters(generator.customFieldDefinitions)) {
    taskInstantiateCustomFieldDefaultValues(customFieldValues, field);
  }

  const task: Task = {
    taskID: generateId('task'),
    taskTemplateID: generator.taskTemplateID,
    taskGeneratorID: generator.taskGeneratorID,
    displayNumber: 0,
    standardTaskIds: generator.standardTaskIds,
    facilityID: generator.facilityID,
    parentFolderIDs: generator.parentFolderIDs ?? [],

    title: generator.title,
    description: generator.description,
    notes: '',
    comments: [],
    category: generator.category ?? 'Other',
    inspectionType: generator.inspectionType ?? 'Other',

    owner: generator.owner,
    involved: generator.involved,
    ownerConfigured: generator.ownerConfigured,
    involvedConfigured: generator.involvedConfigured,

    customFieldDefinitions: generator.customFieldDefinitions.map((d) => {
      const result = { ...d };
      delete result.defaultValue;
      return result;
    }),
    customFieldValues,
    files: [],

    tracked: generator.tracked,
    overdue: dueAt < today,

    dueAt,
    completedAt: null,
    completedBy: null,
    createdAt: timestamp(now),
    createdBy,
    updatedAt: timestamp(now),
    updatedBy: createdBy,
    deletedAt: null,
    deletedBy: null,
  };

  const dueAtAdjusted = generator.dueDateAdjustments?.[dueAt];
  if (dueAtAdjusted) {
    task.dueAtOrig = dueAt;
    task.dueAt = dueAtAdjusted;
  }

  if (generator.schedule.dueAtFirst.time) {
    task.dueAtTime = generator.schedule.dueAtFirst.time;
  }

  return task;
}

export function taskInstantiateCustomFieldDefaultValues(
  customFieldValues: Task['customFieldValues'],
  field: TaskCustomField | TaskCustomFieldWithDefaultValue,
): void {
  if ('defaultValue' in field && field.defaultValue !== undefined) {
    customFieldValues[field.fieldID] = field.defaultValue;
  }
}

/**
 * Given a Task for a given TaskGenerator, figure out what the due date of the next task in the series would be.
 * This will return null if the generator would reach its limit. This will return original due dates NOT adjusted
 * due dates.
 */
export function taskNextDueDate(generator: TaskGenerator, task: Task, timeZone: string): ISO8601DateOnly | null {
  const currentDueAt = task.dueAtOrig ?? task.dueAt;
  // Walk forward two steps to ensure we'll generate the next task (two instead of one because if we're anchored
  // to the end of the month and the current task was due on February 28th, we run the risk of not going far enough):
  const end = dateStrDurationAdd(
    dateStrDurationAdd(currentDueAt, generator.schedule.frequency),
    generator.schedule.frequency,
  );
  const { dueDates } = scheduleToSetOfTaskDueDates(
    generator.schedule,
    currentDueAt,
    new Set(generator.dueDatesExcluded ?? []),
    new Date(end),
    timeZone,
    generator.dueDateAdjustments ?? {},
  );
  dueDates.delete(currentDueAt);
  return Array.from(dueDates).sort((a, b) => a.localeCompare(b))[0] || null;
}

export function taskHasCustomFieldCompleted(
  values: Task['customFieldValues'],
  method9ById: Map<Method9Values['method9ID'], Method9Values>,
  field: TaskCustomField,
): boolean {
  if (!(field.fieldID in values)) {
    return false;
  }

  if (
    (field.type === 'text' || field.type === 'textarea') &&
    typeof values[field.fieldID] === 'string' &&
    !values[field.fieldID].trim()
  ) {
    return false;
  }

  if (field.type === 'select' && !field.options.find((o) => o.value === values[field.fieldID])) {
    return false;
  }

  if (
    (field.type === 'image' || field.type === 'file') &&
    Array.isArray(values[field.fieldID]) &&
    values[field.fieldID].length === 0
  ) {
    return false;
  }

  if (field.type === 'method9') {
    if (!values[field.fieldID]) {
      return false;
    }

    if (!method9ById.has(values[field.fieldID])) {
      return false;
    }

    const method9 = method9ById.get(values[field.fieldID])!;

    if (method9.emissionsUnitRanDuringDaylight) {
      if (
        method9.observations === null ||
        !method9ObservationsAreComplete(method9.observations, method9.stageBoundaries, field.stages).isComplete ||
        method9.signature === null ||
        new Set(method9.diagram.items.map((i) => i.type)).size < 4
      ) {
        return false;
      }
    }
  }

  if (field.type === 'confirmPermitStatus') {
    if (!values[field.fieldID]) {
      return false;
    }

    if (!values[field.fieldID].isComplete) {
      return false;
    }
  }

  return true;
}

export function taskHasAllCustomFieldsCompleted(
  task: Task,
  method9ById: Map<Method9Values['method9ID'], Method9Values>,
): boolean {
  if (task.customFieldDefinitions.length === 0) {
    return true;
  }

  for (const field of task.customFieldDefinitions) {
    // Disallow completing tasks that contain repeaters with no instances, as this indicates a configuration mistake:
    if (field.type === 'repeater' && field.instances.length === 0) {
      return false;
    } else if (field.type === 'lock' && task.customFieldValues[field.fieldID] === true) {
      return false;
    }
  }

  for (const field of customFieldsExpandAllRepeaters(task.customFieldDefinitions)) {
    if (!taskHasCustomFieldCompleted(task.customFieldValues, method9ById, field)) {
      return false;
    }
  }

  return true;
}

export function taskCountCustomFields(
  task: Pick<Task, 'customFieldDefinitions' | 'customFieldValues'>,
  method9ById: Map<Method9Values['method9ID'], Method9Values>,
): { total: number; answered: number } {
  let total = 0;
  let answered = 0;

  for (const field of task.customFieldDefinitions) {
    // Mark a repeater with no instances as missing an answer, as this indicates a configuration mistake:
    if (field.type === 'repeater' && field.instances.length === 0) {
      total += 1;
    }
  }

  for (const field of customFieldsExpandAllRepeaters(task.customFieldDefinitions)) {
    total += 1;
    if (taskHasCustomFieldCompleted(task.customFieldValues, method9ById, field)) {
      answered += 1;
    }
  }

  return { total, answered };
}

export function taskEscalationHierarchyCreate(
  task: Pick<Task, 'owner' | 'involved' | 'ownerConfigured' | 'involvedConfigured' | 'facilityID'>,
  tracked: TrackedStatus,
  config: FacilityHierarchyConfig,
  allUsers: Map<Uid, User>,
): TaskEscalationHierarchy {
  const result: TaskEscalationHierarchy = {
    owner: {
      users: uidsToUsers(allUsers, [task.owner]),
      escalates: null,
      source: 'builtin',
      name: 'Owner',
    },
    involved: {
      users: uidsToUsers(allUsers, task.involved),
      escalates: null,
      source: 'builtin',
      name: 'Involved',
    },

    environmentalManagers: { ...config.environmentalManagers, users: [], escalates: null },
    generalManagers: { ...config.generalManagers, users: [], escalates: null },
    corporateEnvironmentalManagers: {
      ...config.corporateEnvironmentalManagers,
      users: [],
      escalates: null,
    },
  };

  if (tracked) {
    for (const [key, item] of Object.entries(config)) {
      const { users, escalates, name, source } = item;
      if (
        'mustBeAssignedToEscalate' in item &&
        item.mustBeAssignedToEscalate &&
        !task.ownerConfigured.includes(key as Role['roleID']) &&
        !task.involvedConfigured.includes(key as Role['roleID'])
      ) {
        continue;
      }

      if (tracked === 'facility' && key == 'corporateEnvironmentalManagers') {
        continue;
      }

      result[key as keyof TaskEscalationHierarchy] = {
        users: uidsToUsers(allUsers, users),
        escalates,
        name,
        source,
      };
    }
  }

  return result;
}

export function getGeneratorDeficiencies(
  taskGenerator: Pick<TaskGenerator, 'schedule' | 'tracked' | 'customFieldDefinitions'>,
  taskTemplates: TaskTemplate[],
): TaskGeneratorDeficiency[] {
  const deficiencies: TaskGeneratorDeficiency[] = [];
  let templateSchedules: ScheduleTemplate[] = [];
  let requiresTracked: TrackedStatus = false;
  let templateHasFacilityScheduleChoice = false;
  const standardTaskIds = new Set<StandardTaskID>();

  for (const taskTemplate of taskTemplates) {
    if (taskTemplate.tracked) {
      // According to Brandon Curtis on 2024-07-31, weekly Water16 tasks do not need to be marked as tracked:
      // According to Brandon Curtis on 2024-08-07, weekly Waste7 tasks do not need to be marked as tracked:
      const ignoringTracked =
        getSimpleDurationLengthInDays(taskGenerator.schedule.frequency) === 7 &&
        (taskTemplate.standardTaskIds[0] === 'Water16' || taskTemplate.standardTaskIds[0] === 'Waste7');

      if (!ignoringTracked) {
        if (!requiresTracked) {
          requiresTracked = taskTemplate.tracked;
        } else if (taskTemplate.tracked === true && requiresTracked === 'facility') {
          requiresTracked = true;
        }
      }
    }
    templateSchedules = templateSchedules.concat(taskTemplate.schedules);

    if (taskTemplate.isFacilityScheduleChoiceAvailable) {
      templateHasFacilityScheduleChoice = true;
    }

    for (const standardTaskId of taskTemplate.standardTaskIds) {
      standardTaskIds.add(standardTaskId);
    }
  }

  const scheduleHasOnce = templateSchedules.find((s) => s.limit === 1);
  templateSchedules.sort(function (a, b) {
    const aDuration = getSimpleDurationLengthInDays(a.frequency);
    const bDuration = getSimpleDurationLengthInDays(b.frequency);

    return bDuration - aDuration;
  });

  if (
    !templateHasFacilityScheduleChoice &&
    templateSchedules.length > 0 &&
    !scheduleHasOnce &&
    getSimpleDurationLengthInDays(taskGenerator.schedule.frequency) >
      getSimpleDurationLengthInDays(templateSchedules[0].frequency)
  ) {
    deficiencies.push({
      deficiencyID: generateId('deficiency'),
      title: 'Task frequency is too low',
      description:
        'This task has a schedule frequency that does not meet the minimum requirements of the standard task.',
      details: {
        type: 'scheduleFrequency',
        currentSchedule: taskGenerator.schedule,
        allowedSchedules: templateSchedules,
      },
    });
  }

  if (requiresTracked && !taskGenerator.tracked) {
    deficiencies.push({
      deficiencyID: generateId('deficiency'),
      title: 'Task is untracked.',
      description: 'This task is required to be tracked.',
      details: {
        type: 'tracked',
        trackedNeeded: requiresTracked,
        standardTaskIds: Array.from(standardTaskIds),
      },
    });
  } else if (requiresTracked === true && taskGenerator.tracked === 'facility') {
    deficiencies.push({
      deficiencyID: generateId('deficiency'),
      title: 'Task is only internally tracked.',
      description:
        'This task is required to be tracked with escalation to corporate, but instead is only tracked internally to the facility.',
      details: {
        type: 'tracked',
        trackedNeeded: requiresTracked,
        standardTaskIds: Array.from(standardTaskIds),
      },
    });
  }

  for (const taskTemplate of taskTemplates) {
    if (taskTemplate.canGeneratorsCustomizeFields) {
      continue;
    }

    for (const taskTemplateCustomField of taskTemplate.customFieldDefinitions) {
      let found = false;

      for (const taskGeneratorCustomField of taskGenerator.customFieldDefinitions) {
        if (taskGeneratorCustomField.label === taskTemplateCustomField.label) {
          found = true;

          if (taskGeneratorCustomField.type !== taskTemplateCustomField.type) {
            deficiencies.push({
              deficiencyID: generateId('deficiency'),
              title: 'Custom field is incorrect',
              description: 'This task has a custom field of the wrong type.',
              details: {
                type: 'wrongCustomFieldType',
                currentCustomField: taskGeneratorCustomField,
                templateCustomField: taskTemplateCustomField,
              },
            });
          }

          break;
        }
      }

      if (!found) {
        deficiencies.push({
          deficiencyID: generateId('deficiency'),
          title: 'Task is missing a custom field',
          description: 'This task is missing a required custom field.',
          details: {
            type: 'missingCustomField',
            customField: taskTemplateCustomField,
          },
        });
      }
    }
  }

  return deficiencies;
}

export function trackedToName(tracked: boolean | 'facility') {
  if (tracked === true) {
    return 'Tracked';
  } else if (tracked === 'facility') {
    return 'Tracked Internally';
  } else {
    return 'Not tracked';
  }
}

export interface TaskTrackedOption {
  value: TrackedStatus;
  label: string;
}

export const taskTrackedRankMapper = new Map<TrackedStatus, number>([
  [false, 0],
  ['facility', 1],
  [true, 2],
]);

// A list of all the possible options and values for `task.tracked`
export const taskTrackedOptions: TaskTrackedOption[] = [
  {
    value: false,
    label: 'No, this is not a tracked task.',
  },
  {
    value: 'facility',
    label: 'Yes, this is tracked internally. It only escalates within the facility',
  },
  {
    value: true,
    label: 'Yes, this is a tracked task that escalates to corporate.',
  },
];

// Returns the selected options that are only equal or above the current value
export function getAvailableTrackedOptions(current: TrackedStatus): TaskTrackedOption[] {
  const currentRank = taskTrackedRankMapper.get(current) ?? 0;
  return taskTrackedOptions.filter((opt) => (taskTrackedRankMapper.get(opt.value) ?? 0) >= currentRank);
}

export function hasLockCustomField(customFields: TaskCustomField[]): boolean {
  for (const field of customFields) {
    if (field.type === 'lock') {
      return true;
    }
  }

  return false;
}
